import { CheckCircle, Clock, Pencil, User, XCircle, Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Alert } from 'react-bootstrap';
import { Atendimento } from '../../types';

interface AtendimentoCardProps {
  atendimento: Atendimento;
  onFinalizar: (id: string, resultado: string, observacoes?: string) => Promise<any>;
  onCancelar: (id: string, motivo?: string) => Promise<any>;
  onEditAtendimento?: (atendimento: Atendimento) => void;
  onConverteuReserva?: (atendimento: Atendimento) => void;
}

const AtendimentoCard: React.FC<AtendimentoCardProps> = ({
  atendimento,
  onFinalizar,
  onCancelar,
  onEditAtendimento,
  onConverteuReserva
}) => {
  const [tempoDecorrido, setTempoDecorrido] = useState<string>('0 min');
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [resultado, setResultado] = useState('');
  const [observacoesFinal, setObservacoesFinal] = useState('');
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [loading, setLoading] = useState(false);


  const getTipoAtendimentoLabel = (tipo: string): string => {
    const tipos: { [key: string]: string } = {
      'atendimento_escolher': 'ATENDIMENTO ESCOLHER',
      'atendimento_escolher_entrega': 'ATENDIMENTO ESCOLHER ENTREGA',
      'entrega': 'ENTREGA',
      'entrega_marcar_barra': 'ENTREGA/MARCAR BARRA',
      'marcar_barra': 'MARCAR BARRA',
      'escolher_infantil': 'ESCOLHER/INFANTIL',
      'escolher': 'ESCOLHER',
      'orcamento': 'ORÇAMENTO'
    };

    return tipos[tipo] || tipo.toUpperCase();
  };

  useEffect(() => {
    const calcularTempo = () => {
      if (!atendimento.horaInicio) return;

      const agora = new Date();
      const [hora, minuto, segundo] = atendimento.horaInicio.split(':').map(Number);

      const inicio = new Date();
      inicio.setHours(hora, minuto, segundo || 0);

      const diffMs = agora.getTime() - inicio.getTime();
      const diffMin = Math.floor(diffMs / 60000);

      if (diffMin < 60) {
        setTempoDecorrido(`${diffMin} min`);
      } else {
        const horas = Math.floor(diffMin / 60);
        const mins = diffMin % 60;
        setTempoDecorrido(`${horas}h ${mins}min`);
      }
    };

    // Calcular imediatamente
    calcularTempo();

    // Atualizar a cada minuto
    const interval = setInterval(calcularTempo, 60000);

    return () => clearInterval(interval);
  }, [atendimento.horaInicio]);

  const handleFinalizar = async () => {
    if (!resultado) {
      alert('Selecione o resultado do atendimento');
      return;
    }

    setLoading(true);
    try {
      await onFinalizar(atendimento.id!, resultado, observacoesFinal || undefined);
      setShowFinalizarModal(false);

      // Se converteu em reserva, chamar callback
      if (resultado === 'converteu_reserva' && onConverteuReserva) {
        setTimeout(() => {
          onConverteuReserva(atendimento);
        }, 100);
      }
    } catch (error) {
      console.error('Erro ao finalizar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = async () => {
    setLoading(true);
    try {
      await onCancelar(atendimento.id!, motivoCancelamento || undefined);
      setShowCancelarModal(false);
    } catch (error) {
      console.error('Erro ao cancelar:', error);
    } finally {
      setLoading(false);
    }
  };




  return (
    <>
      <div
        className="atendimento-card-modern"
      >
        {/* Cabeçalho */}
        <div className="atendimento-card-header">
          <div>
            <div className="atendimento-card-title">
              <User size={18} />
              {atendimento.funcionarioNome.toUpperCase()}
            </div>
            <div className="atendimento-card-badge mt-2">
              {getTipoAtendimentoLabel(atendimento.tipoAtendimento)}
            </div>
          </div>
          <div className="atendimento-card-time">
            <Clock size={16} />
            {tempoDecorrido}
          </div>
        </div>

        {/* Informações */}
        <div className="atendimento-card-info">
          <div className="atendimento-card-info-item">
            <User size={18} />
            <span><strong>Cliente:</strong> {atendimento.clienteNome}</span>
          </div>

          <div className="atendimento-card-info-item">
            <Clock size={18} />
            <span><strong>Início:</strong> {atendimento.horaInicio}</span>
          </div>

          {atendimento.observacoes && (
            <div className="atendimento-card-obs mt-2">
              <strong>Observações:</strong>
              <div style={{
                whiteSpace: 'pre-line',
                marginTop: '4px',
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                {atendimento.observacoes}
              </div>
            </div>
          )}

          {/* Exibir produtos selecionados se existirem */}
          {atendimento.produtosSelecionados && atendimento.produtosSelecionados.length > 0 && (
            <div className="atendimento-card-produtos mt-2">
              <strong>Produtos Selecionados:</strong>
              <div style={{ marginTop: '4px' }}>
                {atendimento.produtosSelecionados.map((produto: {
                  id: string;
                  titulo: string;
                  tamanho: string;
                  cor: string;
                  codigoProduto: string;
                  observacao?: string;
                }, index: number) => (
                  <div key={index} style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '2px'
                  }}>
                    • {produto.codigoProduto ? `[${produto.codigoProduto}] ` : ''}
                    {produto.titulo} - {produto.tamanho} - {produto.cor} - {produto.observacao}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>


        {/* Botões */}
        <div className="atendimento-card-actions">
          <button
            className="atendimento-card-btn atendimento-card-btn-success"
            onClick={() => setShowFinalizarModal(true)}
          >
            <CheckCircle size={18} />
            Finalizar
          </button>
          <button
            className="atendimento-card-btn atendimento-card-btn-danger"
            onClick={() => setShowCancelarModal(true)}
          >
            <XCircle size={18} />
            Cancelar
          </button>
          <button
            className="atendimento-card-btn atendimento-card-btn-primary"
            onClick={() => onEditAtendimento?.(atendimento)}
            title="Editar atendimento"
          >
            <Pencil size={18} />
            Editar
          </button>
        </div>
      </div>

      {/* Modal Finalizar */}
      <Modal show={showFinalizarModal} onHide={() => setShowFinalizarModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Finalizar Atendimento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Resultado do Atendimento *</Form.Label>
            <Form.Select
              value={resultado}
              onChange={(e) => setResultado(e.target.value)}
              required
            >
              <option value="">Selecione...</option>
              {/* <option value="finalizou_contrato">Finalizou Contrato</option> */}
              <option value="nao_pagou">Não Fez Pagamento</option>
              <option value="nao_levou">Não Levou</option>
              <option value="so_orcamento">Só Orçamento</option>
              <option value="converteu_reserva">Converteu em Reserva</option>
              <option value="outros">Outros</option>
            </Form.Select>
          </Form.Group>


          <Alert variant="info" className="d-flex align-items-start gap-2 mb-3">
            <Info size={20} className="mt-1 flex-shrink-0" />
            <div>
              <strong>Informação importante:</strong>
              <p className="mb-0 small">
                Ao selecionar "Converteu em Reserva", você será redirecionado automaticamente
                para criar a reserva.
              </p>
            </div>
          </Alert>


          <Form.Group>
            <Form.Label className="fw-semibold">Observações Finais</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Adicione observações sobre o atendimento..."
              value={observacoesFinal}
              onChange={(e) => setObservacoesFinal(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFinalizarModal(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleFinalizar} disabled={loading || !resultado}>
            {loading ? 'Finalizando...' : 'Finalizar Atendimento'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Cancelar */}
      <Modal show={showCancelarModal} onHide={() => setShowCancelarModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancelar Atendimento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja cancelar este atendimento?</p>
          <Form.Group>
            <Form.Label className="fw-semibold">Motivo do Cancelamento</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Informe o motivo (opcional)..."
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelarModal(false)} disabled={loading}>
            Voltar
          </Button>
          <Button variant="danger" onClick={handleCancelar} disabled={loading}>
            {loading ? 'Cancelando...' : 'Confirmar Cancelamento'}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default AtendimentoCard;

